import React, { useState, ReactNode } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { Button, Divider, Modal, CircularProgress } from '@mui/material';
import { Icon } from '@components/icon';
import { TERMS } from 'components/auth-template/constants/terms';
import { PRIVACY_POLICY } from '@components/auth-template/constants/privacy-policy';

import * as S from './styles';

type Props = {
  title: string;
  children: ReactNode;
  buttonText: string;
  buttonOnClick: () => void;
  isLoading?: boolean;
};

type TModal = undefined | { title: string; description: string };

export function AuthTemplate({
  title,
  children,
  buttonText,
  buttonOnClick,
  isLoading = false,
}: Props) {
  const location = useLocation();
  const navigate = useNavigate();

  const isSignedUp = location.pathname === '/sign-up';

  const [modalContent, setModalContent] = useState<TModal>();

  const onNavigateToSignUp = () => {
    navigate('/sign-up');
  };

  return (
    <S.Container>
      <S.LeftContent>
        <S.CircularEffect />
        <S.CircularEffectInside />
        <S.LogoIconLight />
      </S.LeftContent>
      <S.RightContent>
        <S.Form sx={{ width: '100%', maxWidth: 450, mx: 'auto', position: 'relative', zIndex: 2 }}>
          {isSignedUp && (
            <Button
              startIcon={<Icon name="arrowLeft" alt="arrow left icon" />}
              sx={{
                fontFamily: 'Mukta',
                fontSize: 20,
                color: 'text.secondary',
                fontWeight: 400,
                pl: 0,
                alignSelf: 'flex-start',
                '&:hover': { color: 'text.primary' },
              }}
              onClick={() => navigate(-1)}
            >
              voltar
            </Button>
          )}

          <Typography
            variant="h4"
            fontFamily="Saira"
            fontWeight={600}
            color="primary.main"
            mb={1}
          >
            {title}
          </Typography>
          {children}
          <Box display="flex" justifyContent="center">
            <S.Button onClick={buttonOnClick} disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} color="inherit" /> : buttonText}
            </S.Button>
          </Box>
          {!isSignedUp && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box display="flex" flexDirection="column" alignItems="center">
                <S.Description mb={2}>Não possui acesso?</S.Description>
                <S.SignUpButton onClick={onNavigateToSignUp}>
                  Cadastrar
                </S.SignUpButton>
              </Box>
            </>
          )}
          <Box display="flex" flexDirection="column" alignItems="center">
            <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" mt={4}>
              <S.UnderlineButton onClick={() => setModalContent(TERMS)}>
                TERMOS DE USO
              </S.UnderlineButton>
              <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
              <S.UnderlineButton onClick={() => setModalContent(PRIVACY_POLICY)}>
                POLÍTICA DE PRIVACIDADE
              </S.UnderlineButton>
            </Box>
            <Typography
              variant="subtitle2"
              fontFamily="Mukta"
              fontWeight={400}
              color="text.secondary"
              mb={2}
              textAlign="center"
            >
              Desenvolvido por Automni Logística Robotizada
            </Typography>
          </Box>
        </S.Form>
      </S.RightContent>
      <Modal
        open={!!modalContent}
        onClose={() => setModalContent(undefined)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <S.ModalContainer>
          <Typography
            variant="h5"
            fontFamily="Saira"
            fontWeight={600}
            color="primary.main"
            textAlign="center"
            mb={2}
          >
            {(modalContent && modalContent?.title) || ''}
          </Typography>
          <S.ModalContent>
            <ReactMarkdown>
              {(modalContent && modalContent?.description) || ''}
            </ReactMarkdown>
          </S.ModalContent>
        </S.ModalContainer>
      </Modal>
    </S.Container>
  );
}
